import { Autocomplete, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import DateRangePicker from '../tools/DateRangePicker';
import MuiServiceTable from './MuiServiceTable';
import ServiceLineChart from './ServiceLineChart';

function isTodayRange(dateRange) {
    const today = new Date();
    return (
        dateRange.from.toDateString() === today.toDateString() &&
        dateRange.to.toDateString() === today.toDateString()
    );
}

const GROUPING_OPTIONS = [
    { label: '1 second', value: '1s', secondsGrouping: 1 },
    { label: '30 seconds', value: '30s', secondsGrouping: 30 },
    { label: '1 minute', value: '1m', secondsGrouping: 60 },
    { label: '30 minutes', value: '30m', secondsGrouping: 1800 },
    { label: '1 hour', value: '1h', secondsGrouping: 3600 },
    { label: '6 hours', value: '6h', secondsGrouping: 21600 },
    { label: '12 hours', value: '12h', secondsGrouping: 43200 },
    { label: '1 day', value: '1d', secondsGrouping: 86400 },
];

const ONE_DAY_IN_SECONDS = 86400;

function ServicesMain(props) {
    const {
        servicesErrorRates,
        isLoadingServicesErrorRates,
        servicesCloudsqlLatency,
        isLoadingServicesCloudsqlLatency,
        servicesLatency,
        isLoadingServicesLatency,
        getServicesErrorRates,
        getServicesCloudsqlLatency,
        getServicesLatency,
        cloudProjects,
        isLoadingCloudProjects,
        getCloudProjects,
        servicesRequestCount,
        isLoadingServicesRequestCount,
        getServicesRequestCount,
    } = props;

    const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
    const [services, setServices] = useState([]);
    const [serviceSelected, setServiceSelected] = useState([]);
    const [projectSelected, setProjectSelected] = useState(null);
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
        to: new Date()
    });
    const [groupingSecondInterval, setGroupingSecondInterval] = useState(ONE_DAY_IN_SECONDS);

    useEffect(() => {
        getCloudProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleServiceRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, projectSelected, groupingSecondInterval]);

    useEffect(() => {
        if (cloudProjects?.length > 0) {
            setProjectSelected(cloudProjects[0]);
        }
    }, [cloudProjects]);

    useEffect(() => {
        if (servicesErrorRates.error_rates) {
            setServices(Array.from(new Set(servicesErrorRates.error_rates.map(service => service.service_name))));
        }
    }, [servicesErrorRates, servicesCloudsqlLatency, servicesLatency, servicesRequestCount]);

    // handlers
    const handleDateRangeChange = (value) => {
        setDateRange(value);
    };

    const handleServiceRequest = () => {
        if (!dateRange || !projectSelected) return;
        const payload = {
            from_date: format(dateRange.from, DATE_FORMAT),
            to_date: format(dateRange.to, DATE_FORMAT),
            project_id: projectSelected?.value,
        };
        if (isTodayRange(dateRange)) {
            payload.alignment_minutes = groupingSecondInterval / 60;
        }

        if (!isLoadingServicesErrorRates) {
            getServicesErrorRates(payload);
        }
        if (!isLoadingServicesCloudsqlLatency) {
            getServicesCloudsqlLatency(payload);
        }
        if (!isLoadingServicesLatency) {
            getServicesLatency(payload);
        }
        if (!isLoadingServicesRequestCount) {
            getServicesRequestCount(payload);
        }
    }

    return (
        <Grid container spacing={1}>
            {/* FORM */}
            <Grid item xs={12} sm={12} md={12}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={2}>
                        <Autocomplete
                            value={projectSelected}
                            options={cloudProjects || []}
                            getOptionLabel={(option) => option?.name || ''}
                            loading={isLoadingCloudProjects}
                            onChange={(e, value) => setProjectSelected(value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Project"
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: '0.75rem',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '0.75rem',
                                            minHeight: '1.5em',
                                            fontSize: '1rem',
                                            padding: '0 10px',
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1rem',
                                            top: '-7px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'transparent',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                            borderWidth: '1px',
                                        }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            borderRadius: '0.75rem',
                                            minHeight: '1.5em',
                                            fontSize: '1rem',
                                            background: '#fff',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            value={serviceSelected}
                            options={services}
                            multiple
                            freeSolo
                            onChange={(e, value) => setServiceSelected(value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Service"
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: '0.75rem',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '0.75rem',
                                            minHeight: '1.5em',
                                            fontSize: '1rem',
                                            padding: '0 10px',
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1rem',
                                            top: '-7px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'transparent',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                            borderWidth: '1px',
                                        }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            borderRadius: '0.75rem',
                                            minHeight: '1.5em',
                                            fontSize: '1rem',
                                            background: '#fff',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <DateRangePicker
                            value={dateRange}
                            onValueChange={handleDateRangeChange}
                            placeholder="Select date range"
                            maxDate={new Date()}
                            minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                        />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <FormControl
                            fullWidth
                            size="small"
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '0.75rem',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '0.75rem',
                                    minHeight: '1.5em',
                                    fontSize: '1rem',
                                    padding: '0 10px',
                                    background: '#fff',
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '1rem',
                                    top: '-7px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#000',
                                    borderWidth: '1px',
                                }
                            }}
                        >
                            <InputLabel id="grouping-interval-label">Grouping Interval</InputLabel>
                            <Select
                                labelId="grouping-interval-label"
                                value={groupingSecondInterval}
                                label="Grouping Interval"
                                disabled={!isTodayRange(dateRange)}
                                onChange={e => setGroupingSecondInterval(e.target.value)}
                            >
                                {GROUPING_OPTIONS.map(opt => (
                                    <MenuItem key={opt.secondsGrouping} value={opt.secondsGrouping}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Grid>

            {/* CHARTS */}
            {/* ERROR RATE */}
            <Grid item xs={12} sm={6} sx={{ m: 0 }}>
                <ServiceLineChart
                    serviceData={servicesErrorRates.error_rates}
                    isLoadingServiceData={isLoadingServicesErrorRates}
                    valueIndex='error_rate_percentage'
                    subtitle='Error rate 5XX over all requests (average rate in a day)'
                    title='Error rate'
                    inputIndex='timestamp'
                    metricType='percent'
                    serviceSelected={serviceSelected}
                />
            </Grid>


            {/* SERVICE TABLE */}
            <Grid item xs={12} md={6}>
                <MuiServiceTable
                    serviceData={servicesRequestCount?.request_count || []}
                    isLoadingServiceData={isLoadingServicesRequestCount}
                    title="Service Request Metrics"
                    subtitle="Overview of service request statistics"
                    serviceSelected={serviceSelected}
                />
            </Grid>

            {/* CLOUDSQL LATENCY */}
            <Grid item xs={12} md={6}>
                <ServiceLineChart
                    serviceData={servicesCloudsqlLatency.cloudsql_latency}
                    isLoadingServiceData={isLoadingServicesCloudsqlLatency}
                    valueIndex='cloudsql_latency'
                    subtitle='Latency between the service and the cloudsql (average in a day)'
                    title='Cloudsql latency'
                    inputIndex='timestamp'
                    dataFormatter={(_) => (inputValue) => {
                        return inputValue + servicesCloudsqlLatency.final_time_measure;
                    }}
                    serviceSelected={serviceSelected}
                />
            </Grid>

            {/* LATENCY */}
            <Grid item xs={12} md={6}>
                <ServiceLineChart
                    serviceData={servicesLatency.latency}
                    isLoadingServiceData={isLoadingServicesLatency}
                    valueIndex='latency'
                    subtitle='Latency of the request (average of all response codes per day including 5XX)'
                    title='Latency'
                    inputIndex='timestamp'
                    dataFormatter={(_) => (inputValue) => {
                        return inputValue + servicesLatency.final_time_measure;
                    }}
                    serviceSelected={serviceSelected}
                />
            </Grid>
        </Grid>
    );
}

export default ServicesMain;
